import { useContext, useEffect, useRef, useState } from "react";
import TenantAuth from "./TenantAuth";
import constants from "../../Utils/constants";
import useTranslator from "../../hooks/useTranslator";
import AuthContext from "../../context/auth/AuthContext";
import i18next from "i18next";
import { haveValue, toast } from "../../helper/common";
import axios from "axios";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TenantContext from "../../context/tenant/TenantContext";
import LandLordContext from "../../context/landlord/LandLordContext";
import LandlordAuth from "./LandlordAuth";
import SwitchUserConfirmationModal from "../../components/SwitchUserConfirmationModal";
import socket from "../../socket";
import { trackClarityEvent } from "../../Utils/clarityReady";
import type {
  AccountType,
  AuthMode,
  AuthScreen,
  UserLoginForm,
} from "./authFlow";
import { createInitialUserLogin, getUserRoleFromPath } from "./authFlow";

const UserAuth = ({ isDummySignup }) => {
  const { _t } = useTranslator();

  const { getUserByEmail, sendOTPES, sendOTP, loadUser } =
    useContext(AuthContext);
  const { shareProfile } = useContext(TenantContext);
  const { getWorkspaceDetailsFromSlug } = useContext(LandLordContext);
  const navigate = useNavigate();
  const queryParameters = new URLSearchParams(window.location.search);
  const workspaceUuid = queryParameters.get("wid");
  const [from, setFrom] = useState<string | null>(null);
  const [landlordsData, setLandlordsData] = useState({});

  useEffect(() => {
    const from = queryParameters.get("from");

    if (from) {
      setFrom(from);
      queryParameters.delete("from");
      const newUrl =
        window.location.pathname +
        (queryParameters.toString() ? "?" + queryParameters.toString() : "");
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  const [userLogin, setUserLogin] = useState<UserLoginForm>(
    createInitialUserLogin()
  );
  const [disabled, setDisabled] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [isUserVerified, setIsUserVerified] = useState(false);
  const [presentUserData, setPresentUserData] = useState({});
  const [userAlreadyExists, setUserAlreadyExists] = useState(false);
  const [currentScreen, setCurrentScreen] = useState<AuthScreen>("email");
  const [cookies, setCookie, removeCookie] = useCookies<string>([
    "sharedProfileLink",
    "requestProfileLink",
  ]);
  const [mode, setMode] = useState<AuthMode>("login");
  const { pathname } = useLocation();
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const { workspace_slug } = useParams();
  const [verifyOtpLoading, setVerifyOtpLoading] = useState(false);
  const [showUserExistsPopup, setShowUserExistsPopup] = useState(false);
  const [switchUserData, setSwitchUserData] = useState<any>({});

  const role = getUserRoleFromPath(pathname);
  const isLandlordFlow = role === "landlord";

  const [currentLang, setCurrentLang] = useState(
    localStorage.getItem("i18nextLng")?.toLowerCase() || "es"
  );

  const handleLanguageChange = async (langVal: string) => {
    await i18next.changeLanguage(langVal);
    setCurrentLang(langVal);
  };

  useEffect(() => {
    const applyLanguage = async () => {
      const queryLang = queryParameters.get("lng");
      const storedLang = localStorage.getItem("i18nextLng");

      if (queryLang === "es") {
        await i18next.changeLanguage("es");
        setCurrentLang("es");
      } else if (storedLang === "es") {
        await i18next.changeLanguage("es");
        setCurrentLang("es");
      } else if (storedLang === "en-us") {
        await i18next.changeLanguage("en-us");
        setCurrentLang("en-us");
      } else {
        await i18next.changeLanguage("es");
        setCurrentLang("es");
      }
    };
    applyLanguage();
  }, []);

  useEffect(() => {
    const accountType: AccountType =
      role === "landlord" ? "landlord" : "tenant";
    localStorage.setItem("usertype", accountType);
    setUserLogin((prev) => ({
      ...prev,
      checkedLandlord: accountType === "landlord",
      checkedTenant: accountType === "tenant",
      type: accountType,
    }));
  }, [role]);

  useEffect(() => {
    if (haveValue(workspace_slug) || haveValue(workspaceUuid)) {
      getWorkspaceDetailsFromSlug(workspace_slug, workspaceUuid)
        .then((res) => {
          const data = res.data;
          if (data.status === "Success") {
            if (
              data?.data?.customLinkEnabled &&
              haveValue(data?.data?.customLinkSlug)
            ) {
              navigate(`/oauth/${data?.data?.customLinkSlug}`);
              if (
                localStorage.getItem("usertype") === "tenant" &&
                haveValue(data?.data?.landlord_id)
              ) {
                setCookie(
                  "requestProfileLink",
                  `${data?.data?.landlord_id}_${data?.data?.uuid}`,
                  {
                    path: "/",
                    maxAge: 604800,
                  }
                );
              }
              setCompanyLogo(data?.data?.invitationLogo);
            } else {
              if (haveValue(data?.data?.invitationLogo)) {
                setCompanyLogo(data?.data?.invitationLogo);
              }
              const url = new URL(window.location.href);
              url.searchParams.delete("wid");

              window.history.replaceState(
                {},
                document.title,
                url.pathname + url.search
              );
            }
            if (data?.data?.catella_rules_activated) {
              setCookie("catellaWorkspaceTenant", `${data?.data?.uuid}`, {
                path: "/",
                maxAge: 3600,
              });
            }
            if (data?.data?.libeen_rules_activated) {
              setCookie("libeenWorkspaceTenant", `${data?.data?.uuid}`, {
                path: "/",
                maxAge: 3600,
              });
            }
            setLandlordsData(data?.data);
          }
        })
        .catch((err) => {
          console.log(err, "Error in getWorkspaceDetailsFromSlug");
        });
    }
  }, [workspace_slug, workspaceUuid]);

  const saveCookieInStateThing = () => {
    if (
      haveValue(cookies.requestProfileLink) &&
      localStorage.getItem("usertype") === "tenant"
    ) {
      setUserLogin((prev) => ({
        ...prev,
        qrCode_signup: true,
        requestProfileLink: haveValue(cookies.requestProfileLink)
          ? cookies.requestProfileLink
          : null,
      }));
    }
  };

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    const temp_var = queryParameters.get("profile_done");
    if (haveValue(temp_var)) {
      setCurrentScreen("landlordBasicInfo");
    }
  }, []);

  useEffect(() => {
    if (isClient) {
      setTimeout(() => {
        saveCookieInStateThing();
      }, 2000);
    }
  }, [isClient]);

  useEffect(() => {
    saveCookieInStateThing();
  }, [cookies?.requestProfileLink]);

  let timer = 60;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [otp, setOtp] = useState("");
  const [showResend, setShowResend] = useState(false);
  const [showTimer, setShowTimer] = useState(timer);

  const validateEmail = (mail: string) => {
    const emailRegex = new RegExp(
      "^[\\p{L}\\p{N}._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
      "u"
    );
    if (emailRegex.test(mail)) {
      return true;
    }
    toast.error(_t("invalidEmailAddress"));
    return false;
  };

  const runTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setShowTimer((prev) => {
        if (prev > 0) {
          return prev - 1;
        } else {
          resetTimer();
          return 0;
        }
      });
    }, 1000);
  };

  const resetTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = null;
    setShowResend(true);
    setShowTimer(60);
  };

  const sendOtpFun = (email = userLogin.email) => {
    if (isDummySignup) {
      resetTimer();
      setShowResend(false);
      runTimer();
      return;
    }
    resetTimer();
    const usertype = localStorage.getItem("usertype");
    if (currentLang === "es") {
      sendOTPES(email, usertype);
    } else {
      sendOTP(email, usertype);
    }
    setOtp("");
    setShowResend(false);
    runTimer();
  };

  const emailSubmitHandler = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const email = userLogin.email;
    const type = userLogin.type;

    if (!validateEmail(email)) {
      return;
    }

    if (isDummySignup) {
      navigate("/demo/onboarding");
      return;
    }

    try {
      setEmailLoading(true);
      setDisabled(true);
      const response = await getUserByEmail({ email, type });
      if (response) {
        if (haveValue(cookies?.invitationToken)) {
          removeCookie("invitationToken", { path: "/" });
        }

        const user = response.user;
        if (user) {
          setUserLogin((prev) => {
            return {
              ...prev,
              _id: user._id,
            };
          });

          setIsUserVerified(user.verified === true);
          setPresentUserData(user);
          setUserAlreadyExists(true);

          if (
            localStorage.getItem("usertype") === "landlord" &&
            user.verified === true
          ) {
            setCurrentScreen("login");
          } else {
            sendOtpFun();
            setCurrentScreen("verifyOtp");
          }

          runTimer();
        } else {
          const userType = type === "tenant" ? "landlord" : "tenant";
          const response2 = await getUserByEmail({ email, type: userType });

          const user = response2?.user;
          if (user) {
            setSwitchUserData(response2);
            setShowUserExistsPopup(true);
          } else {
            if (type === "tenant") {
              // NEW FLOW: email -> OTP -> basic info
              setMode("signup");
              sendOtpFun();
              setCurrentScreen("verifyOtp");
              runTimer();
              trackClarityEvent("tenant_onboarding_started");
              //   @ts-ignore
            } else if (type !== "tenant") {
              setCurrentScreen("signup");
              setMode("signup");
              trackClarityEvent("landlord_onboarding_started");
              setTimeout(() => {
                trackClarityEvent("landlord_password_step_started");
              }, constants.CLARITY_DELAY);
            }
          }
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setEmailLoading(false);
      setDisabled(false);
    }
  };

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.name === "email") {
      sessionStorage.setItem("dummy_email", event.target.value.toLowerCase());
      setUserLogin({
        ...userLogin,
        [event.target.name]: event.target.value.toLowerCase(),
      });
    } else {
      setUserLogin({
        ...userLogin,
        [event.target.name]: event.target.value,
      });
    }
  };

  const verifyOtpHandler = async (
    event: React.FormEvent<HTMLFormElement>,
    createUser?: () => Promise<void>
  ): Promise<void> => {
    event.preventDefault();

    if (isDummySignup && otp.length === 6) {
      navigate("/demo/onboarding");
      return;
    }

    try {
      if (haveValue(otp) && otp.length === 6) {
        setVerifyOtpLoading(true);
        const response = await axios.put(
          `${process.env.REACT_APP_SERVER_URL}/users/otp-verify/${userLogin.email}`,
          { type: userLogin.type, otp: otp },
          {
            headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
          }
        );
        if (response.data) {
          if (mode === "login" && isUserVerified) {
            redirectFun(response.data);
          } else if (
            userLogin.type === "tenant" &&
            mode === "signup" &&
            !userAlreadyExists &&
            !isUserVerified
          ) {
            // tenant signup OTP verified -> go to basic info, don't create user yet
            if (response.data.status === "verified") {
              localStorage.setItem("is_verified", "true");
              if (haveValue(response?.data?.token)) {
                localStorage.setItem("token", response.data.token);
                localStorage.setItem(
                  "saveduserid",
                  response.data.user?._id ?? ""
                );
              }
              setIsUserVerified(true);
              setCurrentScreen("signup");
              trackClarityEvent("tenant_basic_info_step_started");
              return;
            }
          } else {
            const usertype = localStorage.getItem("usertype");
            if (response.data.status === "verified") {
              if (!haveValue(response?.data?.user?.email)) {
                await createUser?.();
              }
              const token = response?.data?.token;
              const saveduserid = response?.data?.user?._id;
              const external_customer_id = response?.data?.user?._id;

              localStorage.setItem("is_verified", "true");
              if (haveValue(token)) {
                localStorage.setItem("token", token);
                localStorage.setItem("is_verified", "true");
                localStorage.setItem("saveduserid", saveduserid);
                localStorage.setItem(
                  "external_customer_id",
                  external_customer_id
                );
              }

              loadUser(usertype);
              if (
                haveValue(cookies.sharedProfileLink) &&
                usertype === "landlord"
              ) {
                if (haveValue(response?.data?.user?._id)) {
                  shareProfile(
                    cookies.sharedProfileLink,
                    response?.data?.user?._id,
                    "null",
                    "full",
                    { shareType: "tLink" }
                  )
                    .then((res) => {
                      removeCookie("sharedProfileLink", { path: "/" });
                    })
                    .catch((err) => {
                      console.log(err);
                    });
                }
              }
              if (
                response?.data?.user?.role === "tenant" ||
                response?.data?.user?.role === "extTenant" ||
                usertype === "tenant"
              ) {
                navigate(
                  response?.data?.user?.profile_completed
                    ? "/home"
                    : "/onboarding?welcome=true"
                );
              }
            }
          }
        }
      } else {
        throw new Error(_t("invalidOtp"));
      }
    } catch (error: any) {
      const serverResponse = error;
      toast.error(serverResponse.response.data.errormsg || _t("invalidOtp"));
    } finally {
      setDisabled(false);
      setVerifyOtpLoading(false);
    }
  };

  const redirectFun = (data: any) => {
    toast.success(_t("loggedInSuccessfully"));

    const token = data.token;
    const is_verified = true;
    const usertype = localStorage.getItem("usertype");
    const saveduserid = data.user._id;
    const external_customer_id = data.user._id;
    const user = data.user;

    if (window.location.host === process.env.REACT_APP_MACC_URL_NAME) {
      if (token && is_verified) {
        const app_type = "macc";
        const queryParams = new URLSearchParams({
          token: token || "",
          usertype: usertype || "",
          is_verified: String(is_verified),
          saveduserid: saveduserid || "",
          external_customer_id: external_customer_id || "",
          app_type: app_type || "",
        });
        document.body.innerHTML = "";
        window.location.replace(
          `${window.location.protocol}//${
            process.env.REACT_APP_URL_NAME
          }/redirect?${queryParams.toString()}`
        );
        return false;
      }
    }

    if (haveValue(cookies.sharedProfileLink) && userLogin.type === "landlord") {
      if (haveValue(data?.user?._id)) {
        shareProfile(
          cookies.sharedProfileLink,
          data?.user?._id,
          "null",
          "full",
          { shareType: "tLink" }
        )
          .then((res) => {
            if (res?.success) {
              toast.success(_t("profileImported"));
            } else {
              toast.error(res?.errormsg ?? _t("failedToGetProfile"));
            }
            removeCookie("sharedProfileLink", { path: "/" });
          })
          .catch((err) => {
            console.log(err);
          });
      }
    }

    localStorage.setItem("token", token);
    localStorage.setItem("is_verified", "true");
    localStorage.setItem("saveduserid", saveduserid);
    localStorage.setItem("external_customer_id", external_customer_id);

    loadUser(userLogin.type);

    if (user.role === "tenant" || usertype === "tenant") {
      navigate(user.profile_completed ? "/home" : "/onboarding?welcome=true");
    } else {
      socket.emit("join_landlord_room", saveduserid);
      if (user?.has_profile && user?.profile_completed) {
        navigate(`/workspace-dashboard`);
      } else if (user?.has_profile) {
        setCurrentScreen("landlordOnboarding");
      } else {
        setCurrentScreen("landlordBasicInfo");
      }
    }
  };

  const handleChangeUser = () => {
    const oppositeType: AccountType =
      userLogin.type === "tenant" ? "landlord" : "tenant";

    navigate(`/${oppositeType}/auth`);
    setShowUserExistsPopup(false);
    if (switchUserData) {
      localStorage.setItem("usertype", oppositeType);
      const user = switchUserData.user;
      if (user) {
        setUserLogin((prev) => {
          return {
            ...prev,
            _id: user._id,
            type: oppositeType,
          };
        });

        setIsUserVerified(user.verified === true);
        setPresentUserData(user);
        setUserAlreadyExists(true);

        if (
          localStorage.getItem("usertype") === "landlord" &&
          user.verified === true
        ) {
          setCurrentScreen("login");
        } else {
          sendOtpFun();
          setCurrentScreen("verifyOtp");
        }

        runTimer();
      }
    }
  };

  const strongPassword = new RegExp(
    "(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})"
  );

  return (
    <>
      <div>
        {isLandlordFlow ? (
          <LandlordAuth
            currentLang={currentLang}
            currentScreen={currentScreen}
            handleLanguageChange={handleLanguageChange}
            emailSubmitHandler={emailSubmitHandler}
            onChangeHandler={onChangeHandler}
            userLogin={userLogin}
            setUserLogin={setUserLogin}
            disabled={disabled}
            setDisableButton={setDisabled}
            setCurrentScreen={setCurrentScreen}
            validateEmail={validateEmail}
            strongPassword={strongPassword}
            runTimer={runTimer}
            sendOtpFun={sendOtpFun}
            otp={otp}
            setOtp={setOtp}
            showResend={showResend}
            showTimer={showTimer}
            verifyOtpHandler={verifyOtpHandler}
            redirectFun={redirectFun}
            userAlreadyExists={userAlreadyExists}
            verifyOtpLoading={verifyOtpLoading}
            from={from}
          />
        ) : (
          <TenantAuth
            emailSubmitHandler={emailSubmitHandler}
            onChangeHandler={onChangeHandler}
            disabled={disabled}
            emailLoading={emailLoading}
            currentScreen={currentScreen}
            setCurrentScreen={setCurrentScreen}
            sendOtpFun={sendOtpFun}
            otp={otp}
            setOtp={setOtp}
            showResend={showResend}
            showTimer={showTimer}
            currentLang={currentLang}
            handleLanguageChange={handleLanguageChange}
            verifyOtpHandler={verifyOtpHandler}
            userLogin={userLogin}
            setUserLogin={setUserLogin}
            runTimer={runTimer}
            companyLogo={companyLogo}
            redirectFun={redirectFun}
            verifyOtpLoading={verifyOtpLoading}
            from={from}
            landlordsData={landlordsData}
            isDummySignup={isDummySignup}
          />
        )}
      </div>
      {showUserExistsPopup && (
        <SwitchUserConfirmationModal
          email={userLogin.email}
          type={userLogin?.type}
          onClose={() => {
            setCurrentScreen("signup");
            setMode("signup");
            setShowUserExistsPopup(false);
          }}
          handleChangeUser={handleChangeUser}
        />
      )}
    </>
  );
};

export default UserAuth;
